import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  ShieldCheck,
  Users,
  BookOpen,
  Award,
  Clock,
  CheckCircle2,
  FileText,
  Compass,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// FD2G-FAC-001 · The Five Voices of the Journey — public landing page
// Program overview + direct pathway into /academy/fd2g-fac-001/enroll.
// Keeps the enrollment / checkout experience deliberately separate.
// ---------------------------------------------------------------------------

export default function AcademyFd2gFacLanding() {
  usePageMeta({
    title: "The Five Voices of the Journey · Certified Facilitator Training | CCCHAPS Academy",
    description:
      "Individual certification pathway for facilitators of the From Diagnosis to Goodbye program.",
  });
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/portal/fd2g-fac-001/pricing`)
      .then((r) => setPricing(r.data))
      .catch(() => setPricing(null));
  }, []);

  const priceLabel = pricing ? `$${Number(pricing.price_usd).toFixed(0)} USD` : "$149 USD";

  return (
    <div className="min-h-screen bg-[#FDFCF6]" data-testid="fd2gfac-landing-page">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 md:pt-20 pb-10">
        <p className="text-xs uppercase tracking-[0.28em] text-[#4A6FA5] font-semibold mb-3">
          CCCHAPS Academy · FD2G-FAC-001
        </p>
        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight"
          data-testid="fd2gfac-landing-title"
        >
          The Five Voices of the Journey
        </h1>
        <p className="mt-3 text-lg text-slate-700 max-w-3xl">
          Certified Facilitator Training for the{" "}
          <em>From Diagnosis to Goodbye</em> curriculum.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-800">
            {pricing?.description || "Founding Facilitator"} · {priceLabel}
          </span>
          <span className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-700">
            Individual Certification
          </span>
          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-900">
            12-Month Credential Term
          </span>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/academy/fd2g-fac-001/enroll"
            data-testid="fd2gfac-landing-enroll-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3d5d8a] transition-colors"
          >
            Enroll — {priceLabel} <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#program"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            Review the program first
          </a>
        </div>
      </section>

      {/* Program overview */}
      <section id="program" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14 border-t border-slate-200">
        <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
          Program
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight max-w-3xl">
          A companioning framework for the five voices that emerge across a
          life-limiting journey.
        </h2>
        <p className="mt-4 text-slate-700 max-w-3xl leading-relaxed">
          From Diagnosis to Goodbye is not a stage model. It is a listening
          practice built around five voices that surface, subside, and return
          across a serious illness — <em>Notice, Listen, Understand, Respond,
          Honor</em>. This certification prepares individual facilitators to
          host the curriculum with fidelity and care.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { icon: Compass, label: "NOTICE", body: "Attune to what is being named — and what is not." },
            { icon: BookOpen, label: "LISTEN", body: "Hold space without correcting or redirecting." },
            { icon: Users, label: "UNDERSTAND", body: "Follow the person, not the diagnosis." },
            { icon: FileText, label: "RESPOND", body: "Move at the pace of the participant." },
            { icon: Award, label: "HONOR", body: "Bring dignity, ethics, and closure to the space." },
          ].map((v) => (
            <div key={v.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <v.icon className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.6} />
              <p className="mt-3 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
                {v.label}
              </p>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What certification includes */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14 border-t border-slate-200">
        <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
          What certification includes
        </p>
        <h2 className="font-heading text-3xl text-slate-900 tracking-tight leading-tight">
          Everything an individual facilitator needs to run the program.
        </h2>
        <ul className="mt-8 grid sm:grid-cols-2 gap-4 text-sm text-slate-700">
          {[
            "Welcome & Orientation with required framework acknowledgements",
            "Five instructional sessions — Notice, Listen, Understand, Respond, Honor",
            "Session-by-session scored assessments (80% pass, remediated on failure)",
            "Code of Ethics and Facilitator Agreement with recorded acceptance",
            "20-question Final Certification Assessment (80% pass · 2 attempts max)",
            "Certified From Diagnosis to Goodbye Facilitator credential with unique credential ID",
            "Downloadable Certificate PDF issued upon successful certification",
            "Facilitator Resource Center — protected access to the three authoritative program PDFs",
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <span className="leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Term & scope */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14 border-t border-slate-200">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
              Credential term
            </p>
            <h3 className="font-heading text-2xl text-slate-900 tracking-tight leading-snug">
              12 months from the date of issue.
            </h3>
            <div className="mt-4 space-y-3 text-sm text-slate-700 leading-relaxed">
              <p className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-slate-500 mt-0.5" />
                <span>
                  Your Certified From Diagnosis to Goodbye Facilitator credential
                  is valid for 12 months from issue. Resource Center access is
                  governed by credential status.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <BookOpen className="w-4 h-4 text-slate-500 mt-0.5" />
                <span>
                  Access to the FD2G-FAC-001 training itself does not
                  auto-expire — you retain the ability to revisit the training
                  materials in your account.
                </span>
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
              What this purchase is — and is not
            </p>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="fd2gfac-landing-scope">
              <p className="text-sm font-semibold text-slate-900">
                Individual certification only.
              </p>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                This purchase certifies one individual to facilitate the
                From Diagnosis to Goodbye curriculum.
              </p>
              <ul className="mt-3 text-sm text-slate-700 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">·</span>
                  Not organizational or site licensing.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">·</span>
                  Not permission to certify other facilitators.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">·</span>
                  Not sublicensing rights.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">·</span>
                  Not organization-wide implementation rights.
                </li>
              </ul>
              <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                Organizational Licensing for the From Diagnosis to Goodbye
                curriculum is a separate FD2G pathway.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust footer + CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14 border-t border-slate-200">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-1">
              Ready to enroll
            </p>
            <p className="font-heading text-2xl text-slate-900 tracking-tight leading-snug">
              Begin certification for {priceLabel}.
            </p>
            <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" />
              Server-verified PayPal Sandbox checkout · access granted only after
              a completed capture.
            </p>
          </div>
          <Link
            to="/academy/fd2g-fac-001/enroll"
            data-testid="fd2gfac-landing-enroll-cta-bottom"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3d5d8a] transition-colors"
          >
            Continue to Enrollment <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
